import type { Schemas } from '@npc/api-client'

import { Card, CardTitle } from '../../components/card'
import { Keys } from './keys'
import { T } from '../../copy/text'
import { daysBetween, hhmm, shortDate } from '../../app/format'
import { t } from '../../copy'
import { useHostAccessMethod } from '../../app/queries'

/**
 * 「万一我这边挂了，你还进得去吗」。
 *
 * ## 这一块只回答一个问题
 *
 * arron 的第一条底线是「服务器管理一定不要被关到外面了，因为很多供应商
 * 客服都找不到，也不带面板」。而平台接入时往他的 `authorized_keys` 里写了一行——
 * **他不知道原来那把还能不能用的话，他就是在把自己往外关，而且是我们帮他关的。**
 *
 * 所以这一块的判据只有一条：**看完之后他知不知道「万一平台挂了，
 * 我还能不能自己 SSH 进去」。**
 *
 * ## 四档各说各的，`unknown` 绝不当成「还能用」
 *
 * `original_access` 是四态不是布尔，因为答案取决于他原来用的是什么：
 *
 * | | 说什么 |
 * |---|---|
 * | `keys` | 进得去，你自己的钥匙还在，我那把是多加的 |
 * | `password` | 进得去，密码登录还开着 |
 * | `none` | **只剩我这一把了——是我早先接管时把你的删掉了** |
 * | `unknown` | **我这次没连上，说不准**——不是说它没了，是我不知道 |
 *
 * `none` 那句刻意认领成我们的错（2026-08-08 之前 takeover 会删掉用户的公钥），
 * 而 `unknown` **绝不能显示成「还能用」**——那是这一页最危险的默认值。
 *
 * ## `live` 为假时那一行必须出现
 *
 * 契约写死了这一条，理由是**一个三天前的「你还进得去」会被当成现在的事实**。
 * 它与 `unknown` 是两件事，不许合并：unknown 是「我不知道」，
 * 这条是「我知道，但那是存档」——**他的下一步一个是等，一个是当场再看一眼。**
 */
export function AccessMethod({
  hostId,
  reach,
}: {
  hostId: string
  reach: Schemas['HostReach'] | undefined
}) {
  const access = useHostAccessMethod(hostId)

  // 取不到就整块不画。**不编一个「你还进得去」出来**——这一块要么说真话，要么不说
  if (!access.data) return null

  return (
    <Card>
      <CardTitle className="mb-npc-grid-sm block">
        <T id="host.access.heading" />
      </CardTitle>

      <p className="m-0 text-npc-body-sm leading-[1.7]">
        <Original access={access.data} reach={reach} />
      </p>

      {/* 「这台机器上现在有哪几把钥匙」。**并在这一块里，不另起一张卡**——
          另起的话他要在两块之间自己拼出「我还进不进得去」，
          而这一块的存在意义就是替他拼好 */}
      <div className="mt-npc-grid-sm">
        <Keys access={access.data} />
      </div>

      {/* 存档那一行。**不是 unknown 的替代品，是它的正交状态**。
          `checked_at` 在契约上是可选的，没有就不画这一行——
          那句话的全部信息量都在「什么时候存的」上，没有时间的版本
          说的是「这是存档」，而那件事光凭一行字他也做不了什么 */}
      {access.data.live || !access.data.checked_at ? null : (
        <p className="mb-0 mt-npc-gap-sm text-npc-caption leading-[1.7] text-npc-amber-ink">
          <T id="host.access.stale" vars={{ checked_at: whenChecked(access.data.checked_at) }} />
        </p>
      )}
    </Card>
  )
}

function Original({
  access,
  reach,
}: {
  access: Schemas['HostAccessMethod']
  reach: Schemas['HostReach'] | undefined
}) {
  switch (access.original_access) {
    case 'keys':
      // 「一共 {count} 把」——后端给的是他自己那些，不含平台与临时钥匙
      return <T id="host.access.original.keys" vars={{ count: access.user_key_count ?? 0 }} />
    case 'password':
      return <T id="host.access.original.password" />
    case 'none':
      return <T id="host.access.original.none" />
    default:
      /**
       * `unknown` 有两个成因，而**它们的句子不一样**。
       *
       * 线上 srv01 就是第二种：平台 30 秒前刚 SSH 进去过（`reach.kind = ok`），
       * 而这一块写着「我这次没能连上这台机器」——**那句话是假的，
       * 而它出现在承载第一条底线的那一块上**。
       *
       * 根因在文案层：`.unknown` 那条**把「为什么不知道」写进了句子里**。
       * 而它 note 里说的重点（「不是说它没了，是我这会儿不知道」）
       * 在两种成因下都成立——**出问题的恰恰是前面那句解释**。
       *
       * 判据用 `reach.kind`（SSH 够不够得到），**不用 `status`**
       * （那说的是探针连没连上，是另一个问题）。
       */
      return reach?.kind === 'ok' ? (
        <T id="host.access.original.unknown_reachable" />
      ) : (
        <T id="host.access.original.unknown" />
      )
  }
}

/**
 * 「什么时候存的」那个变量。**模板要的是人话，不是一个日期。**
 *
 * 「这是 8/9 存下来的」——8/9 对 arron 不是人话，他得先想一下那是哪天。
 * 近两天走 `host.recent.today` / `.yesterday` 两条现成模板再拼上时刻，
 * 再往前才给日期。**与机器详情右下那一列用的是同一套判法**
 * （`whenLabel`），两处说法不许分家。
 */
function whenChecked(at: string): string {
  const days = daysBetween(at, new Date())
  if (days <= 0) return `${t('host.recent.today')} ${hhmm(at)}`
  if (days === 1) return `${t('host.recent.yesterday')} ${hhmm(at)}`
  return shortDate(at)
}
